import type { Period } from '#/shared/api/generated/analytics/models'

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE_RE.test(value)
}

/** Local-date `YYYY-MM-DD` — avoids the UTC shift `toISOString()` would cause. */
export function toIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function parseIsoDate(value: string | undefined): Date | undefined {
  if (!isIsoDate(value)) return undefined
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export interface CustomDateRange {
  from?: string
  to?: string
}

/** Both ends present → the request may fire (the backend 400s on a half-range). */
export function isCompleteRange(range: CustomDateRange | undefined): boolean {
  return Boolean(range?.from) && Boolean(range?.to)
}

export interface RangeQuery {
  params: { period: Period; date_from?: string; date_to?: string }
  /** Whether the query should fire — custom ranges wait for both ends. */
  enabled: boolean
  /** Custom selected but the range is still incomplete. */
  awaitingCustomRange: boolean
}

/**
 * Resolves the dashboard period request. `custom` carries `date_from`/`date_to`
 * and is held back until both are set; preset periods always fire.
 */
export function resolveRangeQuery(
  period: Period,
  isCustom: boolean,
  range: CustomDateRange,
): RangeQuery {
  const ready = isCompleteRange(range)
  return {
    params: isCustom
      ? { period, date_from: range.from, date_to: range.to }
      : { period },
    enabled: !isCustom || ready,
    awaitingCustomRange: isCustom && !ready,
  }
}
